import Drawer from "@mui/material/Drawer";
import { Button, Dropdown, Icon, InputItem } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_SURVEY_SOURCES,
  EMPTY_SURVEY,
  isFormValid,
  validateSurveyForm,
} from "./surveyDrawerUtils";
import { useSurveyProviders } from "./useSurveyProviders";
import "./surveyDetailsDrawer.scss";

const RequiredLabel = ({ children, t, info, required = true }) => (
  <div className="survey-details-drawer__field-label">
    <span className="survey-details-drawer__field-label-text">
      {children}
      {info && (
        <Icon
          className="survey-details-drawer__field-label-info"
          icon="info"
          size="small"
        />
      )}
    </span>
    {required && (
      <span className="required-marker">*{t("SurveyManagement:Required")}</span>
    )}
  </div>
);

const SurveyDetailsDrawer = ({
  open,
  mode,
  initialValues,
  existingSurveys = [],
  editingPlatformId = null,
  onSave,
  onCancel,
  saving,
}) => {
  const { t } = useTranslation();
  const providers = useSurveyProviders();
  const providersDisabled = providers.length === 0;

  const [values, setValues] = useState(EMPTY_SURVEY);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY_SURVEY, ...(initialValues || {}) });
      setTouched({});
    } else {
      setValues(EMPTY_SURVEY);
      setTouched({});
    }
  }, [open, initialValues]);

  // Uniqueness is checked against the program's existing surveys; in edit mode the
  // survey being edited is excluded so it never flags itself as a duplicate.
  const validationOptions = { existingSurveys, editingPlatformId };
  const errors = validateSurveyForm(values, validationOptions);
  const canSave = isFormValid(values, validationOptions) && !saving;

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const markTouched = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const showError = (key) => touched[key] && errors[key];

  const dropdownOptions = providers.map((p) => ({
    id: p.id,
    displayText: p.name,
  }));

  const selectedSource = values.source
    ? dropdownOptions.filter((o) => o.id === values.source)
    : [];

  const handleSave = () => {
    if (!canSave) {
      setTouched({
        source: true,
        surveyName: true,
        surveyId: true,
        surveyUrl: true,
      });
      return;
    }
    onSave({
      source: values.source,
      surveyName: values.surveyName.trim(),
      surveyId: values.surveyId.trim(),
      surveyUrl: values.surveyUrl.trim(),
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      anchor="right"
      classes={{ paper: "survey-details-drawer" }}
    >
      <div className="survey-details-drawer__header">
        <h2 className="survey-details-drawer__title">
          {mode === "edit"
            ? t("SurveyManagement:DrawerTitleEdit")
            : t("SurveyManagement:DrawerTitleCreate")}
        </h2>
        <button
          type="button"
          aria-label="Close"
          className="survey-details-drawer__close"
          onClick={onCancel}
        >
          <Icon icon="close" size="medium" />
        </button>
      </div>

      <div className="survey-details-drawer__body">
        <div className="survey-details-drawer__fields-card">
          <div>
            <RequiredLabel t={t} info>
              {t("SurveyManagement:Source")}
            </RequiredLabel>
            {providersDisabled ? (
              <div
                className="survey-details-drawer__field-error"
                data-testid="no-providers-msg"
              >
                {t("SurveyManagement:NoProvidersConfigured")}
              </div>
            ) : (
              <Dropdown
                data-testid="source-dropdown"
                childList={dropdownOptions}
                label=""
                placeholder={t("SurveyManagement:PlaceholderSelect")}
                selectedValue={selectedSource}
                selection="single"
                setSelectedValue={(selected) => {
                  const next = Array.isArray(selected) ? selected[0]?.id || "" : "";
                  setField("source", next);
                  markTouched("source");
                }}
              />
            )}
            {showError("source") && (
              <div className="survey-details-drawer__field-error">
                {t(`SurveyManagement:${errors.source}`)}
              </div>
            )}
          </div>

          <div>
            <RequiredLabel t={t}>{t("SurveyManagement:Name")}</RequiredLabel>
            <InputItem
              data-testid="name-input"
              placeholder={t("SurveyManagement:PlaceholderName")}
              size="medium"
              value={values.surveyName}
              onChange={(e) => {
                const next = typeof e === "string" ? e : (e?.target?.value ?? "");
                setField("surveyName", next);
              }}
              onBlur={() => markTouched("surveyName")}
            />
            {showError("surveyName") && (
              <div className="survey-details-drawer__field-error">
                {t(`SurveyManagement:${errors.surveyName}`)}
              </div>
            )}
          </div>

          <div>
            <RequiredLabel t={t}>{t("SurveyManagement:SurveyId")}</RequiredLabel>
            <InputItem
              data-testid="survey-id-input"
              placeholder={t("SurveyManagement:PlaceholderSurveyId")}
              size="medium"
              value={values.surveyId}
              onChange={(e) => {
                const next = typeof e === "string" ? e : (e?.target?.value ?? "");
                setField("surveyId", next);
              }}
              onBlur={() => markTouched("surveyId")}
            />
            {showError("surveyId") && (
              <div className="survey-details-drawer__field-error">
                {t(`SurveyManagement:${errors.surveyId}`)}
              </div>
            )}
          </div>

          <div>
            <RequiredLabel
              t={t}
              required={
                DEFAULT_SURVEY_SOURCES.find((s) => s.id === values.source)
                  ?.urlRequired !== false
              }
            >
              {t("SurveyManagement:SurveyUrl")}
            </RequiredLabel>
            <InputItem
              data-testid="survey-url-input"
              placeholder={t("SurveyManagement:PlaceholderSurveyUrl")}
              size="medium"
              value={values.surveyUrl}
              onChange={(e) => {
                const next = typeof e === "string" ? e : (e?.target?.value ?? "");
                setField("surveyUrl", next);
              }}
              onBlur={() => markTouched("surveyUrl")}
            />
            {showError("surveyUrl") && (
              <div className="survey-details-drawer__field-error">
                {t(`SurveyManagement:${errors.surveyUrl}`)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="survey-details-drawer__footer">
        <Button variant="tertiary" size="medium" onClick={onCancel}>
          {t("SurveyManagement:Cancel")}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleSave}
          disabled={!canSave}
        >
          {t("SurveyManagement:Save")}
        </Button>
      </div>
    </Drawer>
  );
};

SurveyDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  initialValues: PropTypes.shape({
    source: PropTypes.string,
    surveyName: PropTypes.string,
    surveyId: PropTypes.string,
    surveyUrl: PropTypes.string,
  }),
  // mapSurveyToRow outputs of the program's existing surveys, used for the
  // Survey ID / Survey URL uniqueness check.
  existingSurveys: PropTypes.array,
  // Platform surveyId of the survey being edited, excluded from the duplicate check.
  editingPlatformId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default SurveyDetailsDrawer;
